/**
 * Shared grouping helpers for Strategy sidebar and Black Ops operatives list.
 */

/** Stable key for strategy sidebar rows (order copy / unowned state). */
export function sidebarMessageGroupKey(msg) {
  const owned = msg.isOwned === false ? 'unowned' : 'owned';
  if (msg.orderId != null) {
    return `${msg.orderId}|${owned}`;
  }
  return `${msg.name}|${msg.cost}|${owned}`;
}

/** Group key for Black Ops operative rows (status + order id). */
export function blackOpsOperativeGroupKey(order) {
  const id = order?.id ?? order?.order_id;
  if (order?._splitKey) {
    if (String(order._splitKey).endsWith('_available')) return `${id}|available`;
    if (String(order._splitKey).includes('_wounded_')) return `${id}|wounded`;
    if (String(order._splitKey).includes('_onmission_')) return `${id}|mission`;
  }
  if (order?.isWounded) return `${id}|wounded`;
  if (order?.isOnMission) return `${id}|mission`;
  return `${id}|available`;
}

/** Whether an order allows only one copy in a deck (API: is_unique). */
export function orderIsUnique(order) {
  return order?.is_unique === true || order?.isUnique === true;
}

/** Badge count for a Black Ops group (available stack uses ownedCount; wounded/mission use row count). */
export function blackOpsOperativeGroupCount(group) {
  const first = group?.items?.[0] ?? group?.item;
  const rowCount = group?.count ?? group?.items?.length ?? 1;

  if (first?._splitKey && String(first._splitKey).endsWith('_available')) {
    return Math.max(1, Number(first.ownedCount) || 1);
  }

  if (rowCount > 1) {
    return rowCount;
  }

  const owned = Number(first?.ownedCount);
  if (Number.isFinite(owned) && owned > 0) {
    return owned;
  }

  return rowCount;
}

/**
 * Count shown in sidebar badge: actual copies (e.g. 3) or 1 for is_unique orders.
 * @param {number} rawCount
 * @param {object | null | undefined} order
 * @returns {number | null}
 */
export function resolveSidebarOrderCountBadge(rawCount, order) {
  const n = Number(rawCount);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  if (orderIsUnique(order)) {
    return n > 0 ? 1 : 0;
  }
  return Math.floor(n);
}

/**
 * Collapse identical sidebar entries for display; preserves first-seen order.
 * @template T
 * @param {T[]} items
 * @param {(item: T) => string} getGroupKey
 * @returns {{ item: T, count: number, firstIndex: number, key: string, items: T[] }[]}
 */
export function groupItemsForSidebarDisplay(items, getGroupKey) {
  const groups = [];
  const keyToGroup = new Map();
  (items || []).forEach((item, index) => {
    const key = getGroupKey(item);
    let group = keyToGroup.get(key);
    if (!group) {
      group = { item, count: 0, firstIndex: index, key, items: [] };
      keyToGroup.set(key, group);
      groups.push(group);
    }
    group.count += 1;
    group.items.push(item);
  });
  return groups;
}

/** @deprecated Use groupItemsForSidebarDisplay(messages, sidebarMessageGroupKey) */
export function groupSidebarMessagesForDisplay(messages) {
  return groupItemsForSidebarDisplay(messages, sidebarMessageGroupKey);
}
